import React, { useState, useEffect } from 'react';
import { adminService } from '../../services/admin';
import { Transaction } from '../../types';
import { Table } from '../../components/ui/Table';
import { Button } from '../../components/ui/Button';
import { Modal, Input, Alert } from '../../components/ui/Modal';
import { statusBadge } from '../../components/ui/Button';
import { formatUSD, formatDate } from '../../utils/formatters';
import { gs } from '../../styles/globalStyles';
import toast from 'react-hot-toast';
import { Ico } from '../../components/ui/Icon';

interface ReviewModalProps {
  tx: Transaction | null;
  onClose: () => void;
  onApprove: (id: number) => void;
  onReject: (id: number, note: string) => void;
  type: 'deposit' | 'withdrawal';
}

const ReviewModal: React.FC<ReviewModalProps> = ({ tx, onClose, onApprove, onReject, type }) => {
  const [note, setNote] = useState('');
  if (!tx) return null;
  return (
    <Modal isOpen={!!tx} onClose={onClose} title={`Review ${type === 'deposit' ? 'Deposit' : 'Withdrawal'} #${tx.id}`} size="lg">
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3 text-sm">
          {[
            { l: 'User', v: tx.user ? `${(tx.user as any).first_name} ${(tx.user as any).last_name} (@${(tx.user as any).username})` : tx.user_id },
            { l: 'Asset', v: `${tx.cryptocurrency} (${tx.network || 'N/A'})` },
            { l: 'Crypto Amount', v: `${tx.amount_crypto} ${tx.cryptocurrency}` },
            { l: 'USD Value', v: formatUSD(tx.amount_usd || 0) },
            { l: type === 'deposit' ? 'TXID' : 'Address', v: type === 'deposit' ? (tx.txid || 'N/A') : (tx.address || 'N/A') },
            { l: 'Status', v: tx.status },
            { l: 'Account', v: tx.wallet_type },
            { l: 'Date', v: formatDate(tx.created_at) },
          ].map(item => (
            <div key={item.l} className="p-3 bg-gray-800/40 rounded-xl">
              <p className="text-gray-500 text-xs">{item.l}</p>
              <p className="text-gray-50 text-sm font-mono mt-1 break-all">{String(item.v)}</p>
            </div>
          ))}
        </div>
        {tx.txid && (
          <a href={`https://blockchair.com/search?q=${tx.txid}`} target="_blank" rel="noopener noreferrer"
            className="text-primary text-xs underline hover:no-underline flex items-center gap-1">
            <Ico name="eye" className="w-3.5 h-3.5" /> Verify on blockchain
          </a>
        )}
        {tx.status === 'pending' && (
          <>
            <Input label="Rejection note (required if rejecting)" placeholder="Reason..." value={note} onChange={e => setNote(e.target.value)} />
            <div className="grid grid-cols-2 gap-3">
              <Button variant="danger" fullWidth icon={<Ico name="close" className="w-3.5 h-3.5" />} onClick={() => { onReject(tx.id, note); onClose(); }}>Reject</Button>
              <Button variant="primary" fullWidth icon={<Ico name="check" className="w-3.5 h-3.5" />} onClick={() => { onApprove(tx.id); onClose(); }}>Approve</Button>
            </div>
          </>
        )}
        {tx.status !== 'pending' && (
          <div className="flex justify-end"><Button variant="ghost" onClick={onClose}>Close</Button></div>
        )}
      </div>
    </Modal>
  );
};

export const AdminDeposits: React.FC = () => {
  const [deposits, setDeposits]   = useState<Transaction[]>([]);
  const [loading, setLoading]     = useState(false);
  const [selected, setSelected]   = useState<Transaction | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const pendingCount = deposits.filter(d => d.status === 'pending').length;

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      // Try to get all transactions, fallback to pending-only
      let data: Transaction[] = [];
      try {
        const res = await adminService.getTransactions({ type: 'deposit', per_page: 100 });
        data = res.data.data || [];
      } catch {
        const res = await adminService.getPendingDeposits();
        data = res.data.data || [];
      }
      setDeposits(data);
    } catch {} finally { setLoading(false); }
  };

  const approve = async (id: number) => {
    try { await adminService.approveDeposit(id); toast.success('Deposit approved'); load(); } catch { toast.error('Failed'); }
  };
  const reject = async (id: number, note: string) => {
    try { await adminService.rejectDeposit(id, note); toast.success('Deposit rejected'); load(); } catch { toast.error('Failed'); }
  };

  const filtered = statusFilter === 'all' ? deposits : deposits.filter(d => d.status === statusFilter);

  return (
    <div className="p-4 md:p-6">
      {pendingCount > 0 && <Alert type="warning" message={`${pendingCount} deposit(s) awaiting review`} className="mb-4" />}

      <div className="flex gap-2 mb-4 items-center flex-wrap">
        <div className="flex gap-1">
          {['all', 'pending', 'approved', 'rejected'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all ${statusFilter === s ? 'bg-primary text-[var(--on-primary)]' : 'bg-gray-800/60 text-gray-400 hover:text-gray-50'}`}>
              {s}
            </button>
          ))}
        </div>
        <button onClick={load} className={gs.btnGhost + ' ml-auto text-xs'}><Ico name="refresh" className="w-3.5 h-3.5" /></button>
      </div>

      <Table
        loading={loading}
        columns={[
          { key: 'user', label: 'User', render: r => <span className="font-semibold text-xs">{(r.user as any)?.username || r.user_id}</span> },
          { key: 'cryptocurrency', label: 'Asset', render: r => <span className="font-mono font-bold text-xs">{r.cryptocurrency} <span className="text-gray-500">({r.network})</span></span> },
          { key: 'amount_crypto', label: 'Amount', render: r => <span className="font-mono text-primary text-xs">{r.amount_crypto} {r.cryptocurrency}</span> },
          { key: 'amount_usd', label: 'USD', render: r => <span className="font-mono text-xs">{formatUSD(r.amount_usd || 0)}</span> },
          { key: 'status', label: 'Status', render: r => statusBadge(r.status) },
          { key: 'wallet_type', label: 'Acct', render: r => <span className={`text-[10px] font-bold ${r.wallet_type === 'real' ? 'text-primary' : 'text-blue-400'}`}>{r.wallet_type?.toUpperCase()}</span> },
          { key: 'created_at', label: 'Date', render: r => <span className="text-xs text-gray-400">{formatDate(r.created_at)}</span> },
          { key: 'actions', label: '', render: r => (
            <Button variant="outline" size="sm" onClick={() => setSelected(r)}>
              {r.status === 'pending' ? 'Review' : 'View'}
            </Button>
          )},
        ]}
        data={filtered}
        keyExtractor={r => r.id}
        emptyMessage="No deposits found"
      />
      <ReviewModal tx={selected} onClose={() => setSelected(null)} onApprove={approve} onReject={reject} type="deposit" />
    </div>
  );
};

export const AdminWithdrawals: React.FC = () => {
  const [withdrawals, setWithdrawals] = useState<Transaction[]>([]);
  const [loading, setLoading]         = useState(false);
  const [selected, setSelected]       = useState<Transaction | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const pendingCount = withdrawals.filter(w => w.status === 'pending').length;

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      let data: Transaction[] = [];
      try {
        const res = await adminService.getTransactions({ type: 'withdrawal', per_page: 100 });
        data = res.data.data || [];
      } catch {
        const res = await adminService.getPendingWithdrawals();
        data = res.data.data || [];
      }
      setWithdrawals(data);
    } catch {} finally { setLoading(false); }
  };

  const approve = async (id: number) => {
    try { await adminService.approveWithdrawal(id); toast.success('Withdrawal approved'); load(); } catch { toast.error('Failed'); }
  };
  const reject = async (id: number, note: string) => {
    try { await adminService.rejectWithdrawal(id, note); toast.success('Withdrawal rejected & refunded'); load(); } catch { toast.error('Failed'); }
  };

  const filtered = statusFilter === 'all' ? withdrawals : withdrawals.filter(w => w.status === statusFilter);

  return (
    <div className="p-4 md:p-6">
      {pendingCount > 0 && <Alert type="warning" message={`${pendingCount} withdrawal(s) awaiting review`} className="mb-4" />}

      <div className="flex gap-2 mb-4 items-center flex-wrap">
        <div className="flex gap-1">
          {['all', 'pending', 'approved', 'rejected'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all ${statusFilter === s ? 'bg-primary text-[var(--on-primary)]' : 'bg-gray-800/60 text-gray-400 hover:text-gray-50'}`}>
              {s}
            </button>
          ))}
        </div>
        <button onClick={load} className={gs.btnGhost + ' ml-auto text-xs'}><Ico name="refresh" className="w-3.5 h-3.5" /></button>
      </div>

      <Table
        loading={loading}
        columns={[
          { key: 'user', label: 'User', render: r => <span className="font-semibold text-xs">{(r.user as any)?.username || r.user_id}</span> },
          { key: 'cryptocurrency', label: 'Asset', render: r => <span className="font-mono font-bold text-xs">{r.cryptocurrency}</span> },
          { key: 'amount_usd', label: 'Amount', render: r => <span className="font-mono text-orange-400 text-xs">{formatUSD(r.amount_usd || 0)}</span> },
          { key: 'address', label: 'Address', render: r => <span className="font-mono text-xs text-gray-400">{r.address?.slice(0, 14)}...</span> },
          { key: 'status', label: 'Status', render: r => statusBadge(r.status) },
          { key: 'wallet_type', label: 'Acct', render: r => <span className={`text-[10px] font-bold ${r.wallet_type === 'real' ? 'text-primary' : 'text-blue-400'}`}>{r.wallet_type?.toUpperCase()}</span> },
          { key: 'created_at', label: 'Date', render: r => <span className="text-xs text-gray-400">{formatDate(r.created_at)}</span> },
          { key: 'actions', label: '', render: r => (
            <Button variant="outline" size="sm" onClick={() => setSelected(r)}>
              {r.status === 'pending' ? 'Review' : 'View'}
            </Button>
          )},
        ]}
        data={filtered}
        keyExtractor={r => r.id}
        emptyMessage="No withdrawals found"
      />
      <ReviewModal tx={selected} onClose={() => setSelected(null)} onApprove={approve} onReject={reject} type="withdrawal" />
    </div>
  );
};
